/* ============================================================================
   IDEA — Pro Staff Chair landing page
   ----------------------------------------------------------------------------
   Vanilla JS, no dependencies, loaded only on the landing template.

   There is no custom order handling here. Every CTA is a native Shopify
   product form; Cart posts it to Shopify's Ajax cart API and the drawer hands
   off to Shopify checkout, which owns payment, cash on delivery and the order
   itself.

     1. Header      — dark glass once the page scrolls
     2. Anchors     — smooth scroll to the offer, offset for the sticky header
     3. Gallery     — thumbnails, arrows, swipe, keyboard, zoom (hero + section)
     4. FAQ         — one answer open at a time (<details> works without this)
     5. StickyCTA   — reveal after the hero, and stay out of everyone's way
     6. Analytics   — dataLayer events for the store's GTM/Shopify setup
     7. Reveal      — IntersectionObserver-driven .lp-reveal
     8. BuyNow      — cart permalink straight to Shopify checkout
     9. Cart        — Ajax add and drawer, for add-to-cart mode
   ========================================================================= */

(function () {
  'use strict';

  var root = document.querySelector('.landing-root');
  if (!root) return;

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  };

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     dataLayer only. No pixel is created here, so the store's Shopify and Meta
     tracking stays the single source of truth.
     --------------------------------------------------------------------- */
  var analyticsPayload = null;

  function push(event, extra) {
    window.dataLayer = window.dataLayer || [];

    if (analyticsPayload === null) {
      var node = document.querySelector('[data-lp-analytics]');
      try { analyticsPayload = node ? JSON.parse(node.textContent) : false; } catch (err) { analyticsPayload = false; }
    }

    var data = { event: event };
    if (analyticsPayload) {
      data.item_id = analyticsPayload.id;
      data.item_sku = analyticsPayload.sku;
      data.item_name = analyticsPayload.title;
      data.value = analyticsPayload.price;
      data.currency = analyticsPayload.currency;
    }
    if (extra) {
      Object.keys(extra).forEach(function (key) { data[key] = extra[key]; });
    }
    window.dataLayer.push(data);
  }

  var CONFIG = window.LP || {};
  var STRINGS = CONFIG.strings || {};
  var ROUTES = CONFIG.routes || {};

  function route(name, fallback) {
    var base = ROUTES[name] || fallback;
    return base.endsWith('.js') ? base : base.replace(/\/$/, '') + '.js';
  }

  /* Campaign money format, matching snippets/landing-money.liquid. */
  function money(cents) {
    var whole = Math.round(Number(cents) / 100);
    var grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return grouped + ' ' + (CONFIG.currencyLabel || '');
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ------------------------------------------------------------------------
     boot() runs again on every shopify:section:load, so the theme editor
     re-renders a section and everything initialises a second time. Handlers
     delegated to `document` survive that re-render, so registering them again
     stacks them: after two re-renders one click on the quantity stepper moved
     it by three, and every dataLayer event fired three times. Anything bound
     to document or window goes through here and is bound once per page.
     --------------------------------------------------------------------- */
  var boundOnce = {};
  function once(key, register) {
    if (boundOnce[key]) return;
    boundOnce[key] = true;
    register();
  }

  /* ========================================================================
     1. HEADER
     ===================================================================== */
  function initHeader() {
    var header = $('[data-lp-sticky-header]');
    if (!header) return;

    var onScroll = function () {
      header.classList.toggle('is-stuck', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ========================================================================
     2. ANCHOR SCROLLING
     ===================================================================== */
  function headerOffset() {
    var header = $('[data-lp-sticky-header]');
    return header ? header.getBoundingClientRect().height : 0;
  }

  function initAnchors() {
    once('anchors', function () {
      document.addEventListener('click', function (e) {
        var link = e.target.closest('[data-lp-scroll]');
        if (!link) return;

        var hash = (link.getAttribute('href') || '').split('#')[1];
        if (!hash) return;

        var target = document.getElementById(hash);
        if (!target) return;

        e.preventDefault();
        var top = target.getBoundingClientRect().top + window.scrollY - headerOffset() - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
      });
    });
  }

  /* ========================================================================
     3. GALLERY
     ===================================================================== */
  function initGallery() {
    $$('[data-lp-gallery]').forEach(setupGallery);
  }

  /* A single scrolling row of product shots. The arrows page the row by whole
     cards; native scrolling (trackpad, touch) keeps working alongside them. */
  function setupGallery(gallery) {
    var track = $('[data-lp-gallery-track]', gallery);
    var slides = $$('[data-lp-gallery-slide]', gallery);
    if (!track || slides.length < 1) return;

    var prev = $('[data-lp-gallery-prev]', gallery);
    var next = $('[data-lp-gallery-next]', gallery);
    var rtl = (root.getAttribute('dir') || 'ltr') === 'rtl';

    function step() {
      var first = slides[0];
      var width = first ? first.getBoundingClientRect().width : track.clientWidth / 3;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 16;
      return width + gap;
    }

    /* scrollLeft runs negative in RTL on standards-compliant browsers, so the
       page direction decides which way "forward" is. */
    function page(direction) {
      var by = step() * (rtl ? -direction : direction);
      if (typeof track.scrollBy === 'function') {
        track.scrollBy({ left: by, behavior: reducedMotion ? 'auto' : 'smooth' });
      } else {
        track.scrollLeft += by;
      }
    }

    function syncArrows() {
      var max = track.scrollWidth - track.clientWidth;
      var at = Math.abs(track.scrollLeft);
      var scrollable = max > 4;

      [prev, next].forEach(function (btn) {
        if (btn) btn.hidden = !scrollable;
      });
      if (!scrollable) return;

      if (prev) prev.disabled = at < 4;
      if (next) next.disabled = at > max - 4;
    }

    if (prev) prev.addEventListener('click', function () { page(-1); });
    if (next) next.addEventListener('click', function () { page(1); });

    track.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows, { passive: true });

    gallery.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var forward = rtl ? e.key === 'ArrowLeft' : e.key === 'ArrowRight';
      page(forward ? 1 : -1);
      e.preventDefault();
    });

    /* Zoom */
    var dialog = $('[data-lp-zoom]');
    if (gallery.hasAttribute('data-lp-gallery-zoom') && dialog && dialog.showModal) {
      var image = $('[data-lp-zoom-image]', dialog);
      var close = $('[data-lp-zoom-close]', dialog);

      gallery.addEventListener('click', function (e) {
        var slide = e.target.closest('[data-lp-gallery-slide]');
        if (!slide) return;

        var full = slide.getAttribute('data-lp-full');
        var img = slide.querySelector('img');
        if (!full || !image) return;

        image.src = full;
        image.alt = img ? img.alt : '';
        dialog.showModal();
      });

      if (close) close.addEventListener('click', function () { dialog.close(); });
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) dialog.close();
      });
    }

    syncArrows();
  }

  /* ========================================================================
     3b. NAVIGATION HIGHLIGHT
     ---------------------------------------------------------------------
     Marks the header link whose section is currently under the header.
     ===================================================================== */
  function initNavHighlight() {
    var links = $$('[data-lp-nav-link]');
    if (!links.length || !('IntersectionObserver' in window)) return;

    var byId = {};
    var targets = [];

    links.forEach(function (link) {
      var id = (link.getAttribute('href') || '').split('#')[1];
      var target = id && document.getElementById(id);
      if (!target) return;
      byId[id] = link;
      targets.push(target);
    });
    if (!targets.length) return;

    var visible = [];

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var at = visible.indexOf(entry.target);
        if (entry.isIntersecting && at === -1) visible.push(entry.target);
        if (!entry.isIntersecting && at !== -1) visible.splice(at, 1);
      });

      // The topmost visible section wins, so the highlight moves in page order.
      var current = visible.slice().sort(function (a, b) {
        return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
      })[0];

      links.forEach(function (link) { link.classList.remove('is-active'); });
      if (current && byId[current.id]) byId[current.id].classList.add('is-active');
    }, { rootMargin: '-25% 0px -55% 0px', threshold: 0 });

    targets.forEach(function (target) { observer.observe(target); });
  }

  /* ========================================================================
     4. FAQ
     ===================================================================== */
  function initFaq() {
    var list = $('[data-lp-faq]');
    if (!list || !list.hasAttribute('data-lp-faq-single')) return;

    var items = $$('.lp-faq__item', list);
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (other) {
          if (other !== item) other.open = false;
        });
      });
    });
  }

  /* ========================================================================
     5. STICKY CTA
     ---------------------------------------------------------------------
     Visible only once the hero is behind the visitor, and hidden whenever it
     would get in the way: over the offer or final CTA (which carry the same
     button), over the footer, while the cart drawer is open, or on top of a
     bottom-fixed banner such as cookie consent or an accessibility widget —
     in that last case the bar lifts above the banner instead.
     ===================================================================== */
  function initStickyCta() {
    var bar = $('[data-lp-sticky]');
    if (!bar) return;

    bar.removeAttribute('hidden');

    var afterId = bar.getAttribute('data-lp-sticky-after') || 'lp-hero';
    var trigger = document.getElementById(afterId);
    var blockers = ['#lp-offer', '#lp-final-cta', '.lp-footer']
      .map(function (sel) { return $(sel); })
      .filter(Boolean);

    var pastTrigger = !trigger;
    var overBlocker = false;
    var cartOpen = false;

    function measure() {
      var height = bar.getBoundingClientRect().height;
      root.style.setProperty('--lp-sticky-h', height + 'px');
    }

    function apply() {
      var visible = pastTrigger && !overBlocker && !cartOpen;
      bar.classList.toggle('is-visible', visible);
      root.classList.toggle('has-sticky-cta', visible);
      if (visible) measure();
    }

    if ('IntersectionObserver' in window) {
      if (trigger) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            // The hero has scrolled off the top of the viewport.
            pastTrigger = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
            apply();
          });
        }, { threshold: 0 }).observe(trigger);
      }

      if (blockers.length) {
        var visibleBlockers = [];
        var blockerObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var at = visibleBlockers.indexOf(entry.target);
            if (entry.isIntersecting && at === -1) visibleBlockers.push(entry.target);
            if (!entry.isIntersecting && at !== -1) visibleBlockers.splice(at, 1);
          });
          overBlocker = visibleBlockers.length > 0;
          apply();
        }, { threshold: 0.12 });
        blockers.forEach(function (el) { blockerObserver.observe(el); });
      }
    } else {
      pastTrigger = true;
    }

    /* Cart drawer: Cart toggles aria-hidden on it. */
    var drawer = $('[data-lp-cart]');
    if (drawer && 'MutationObserver' in window) {
      var syncCart = function () {
        cartOpen = drawer.getAttribute('aria-hidden') === 'false';
        apply();
      };
      new MutationObserver(syncCart).observe(drawer, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
      syncCart();
    }

    /* Third-party bottom banners (cookie consent, accessibility widgets).
       Anything fixed to the bottom of the viewport pushes our bar up. */
    function avoidBottomOverlays() {
      var offset = 0;
      var viewportH = window.innerHeight;

      $$('body > *').forEach(function (el) {
        if (el === bar || bar.contains(el) || el.contains(bar)) return;
        if (el.hasAttribute('hidden')) return;

        var style = window.getComputedStyle(el);
        if (style.position !== 'fixed' || style.display === 'none' || style.visibility === 'hidden') return;

        var rect = el.getBoundingClientRect();
        if (rect.height < 24 || rect.width < window.innerWidth * 0.5) return;
        if (rect.bottom < viewportH - 4 || rect.top > viewportH) return;

        offset = Math.max(offset, Math.min(rect.height, viewportH * 0.5));
      });

      bar.style.insetBlockEnd = offset ? offset + 'px' : '';
      measure();
    }

    apply();
    measure();

    window.addEventListener('resize', function () { measure(); avoidBottomOverlays(); }, { passive: true });
    window.addEventListener('load', avoidBottomOverlays);
    // Consent banners are usually injected a beat after load.
    setTimeout(avoidBottomOverlays, 1200);
    setTimeout(avoidBottomOverlays, 4000);
  }

  /* ========================================================================
     6. ANALYTICS
     ---------------------------------------------------------------------
     Pushes to the existing GTM dataLayer only. No pixel is instantiated here,
     so the store's Shopify/Meta setup stays the single source of truth for
     PageView, ViewContent, AddToCart, InitiateCheckout and Purchase.
     ===================================================================== */
  function initAnalytics() {
    once('analytics', function () {
      push('landing_view_item');

      document.addEventListener('click', function (e) {
        var tracked = e.target.closest('[data-lp-track]');
        if (!tracked) return;
        push('landing_cta_click', { cta: tracked.getAttribute('data-lp-track') });
      });
    });

    /* The theme's cart drawer handles the actual add; we only record it. */
    /* Buy-now reports from its own handler, which stops propagation before
       this one would run. */
    $$('form.lp-buy').forEach(function (form) {
      if (form.hasAttribute('data-lp-tracked')) return;
      form.setAttribute('data-lp-tracked', '');
      form.addEventListener('submit', function () {
        var button = form.querySelector('[data-lp-atc]');
        var qtyField = form.querySelector('[name="quantity"]');
        push('landing_add_to_cart', {
          cta: button ? button.getAttribute('data-lp-context') : 'landing',
          variant_id: button ? button.getAttribute('data-lp-variant-id') : null,
          quantity: Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1)
        });
      });
    });
  }

  /* ========================================================================
     6b. QUANTITY STEPPER
     ---------------------------------------------------------------------
     The input is a plain number field inside the Shopify product form, so it
     still works without this — the buttons are only a nicer way to change it.
     ===================================================================== */
  function bounds(input) {
    var min = parseInt(input.getAttribute('min'), 10);
    var max = parseInt(input.getAttribute('max'), 10);
    return {
      min: isNaN(min) ? 1 : min,
      max: isNaN(max) ? Infinity : max
    };
  }

  function clampQty(input, value) {
    var b = bounds(input);
    if (isNaN(value)) value = b.min;
    return Math.min(b.max, Math.max(b.min, value));
  }

  /* Dim the button that would take the visitor past a limit.

     Deliberately aria-disabled and a class rather than the disabled property:
     a disabled button cannot be clicked, so if this state is ever out of date
     it stops the visitor dead. It can go out of date — browsers restore form
     fields when someone returns to the page, without firing an event — and a
     visitor who came back to find 3 in the field could not lower it, because
     the minus button was still disabled from when the field held 1. Dimmed but
     live is recoverable: the click goes through and puts everything right. */
  function refreshStepper(input) {
    var control = input.parentElement;
    var b = bounds(input);
    var value = parseInt(input.value, 10) || b.min;

    [['[data-lp-qty-down]', value <= b.min], ['[data-lp-qty-up]', value >= b.max]]
      .forEach(function (pair) {
        var btn = control.querySelector(pair[0]);
        if (!btn) return;
        /* Styling only. No disabled, and no aria-disabled either — both tell a
           visitor the button cannot be used, and this button always can. The
           bounds a visitor needs to know are on the input's min and max. */
        btn.classList.toggle('is-limit', pair[1]);
      });
  }

  /* Every CTA on the page buys the same line, so they all carry the same
     quantity — including the hidden fields behind the sticky bar. Otherwise
     choosing 3 in the hero and then tapping the sticky button would quietly
     order 1. */
  function applyQty(value) {
    $$('form.lp-buy [name="quantity"]').forEach(function (field) {
      field.value = value;
    });
    $$('form.lp-buy .lp-qty__input').forEach(refreshStepper);
  }

  /* Read whatever is in the visible field right now and make the rest of the
     page agree with it.

     This has to be callable at any time, not just at startup. Browsers restore
     form fields when a visitor returns to a page, and they do it on their own
     schedule — often after DOMContentLoaded. A visitor who ordered 3 and came
     back would find the field showing 3 while the minus button was still
     disabled from the moment it held 1, so the quantity could not be lowered,
     and the hidden fields behind the other buttons still said 1. */
  function syncQty() {
    var stepper = $('form.lp-buy .lp-qty__input');
    if (!stepper) return;
    applyQty(clampQty(stepper, parseInt(stepper.value, 10)));
  }

  function initQuantity() {
    if (!$('form.lp-buy .lp-qty__input')) return;

    once('quantity', function () {
      /* Catch a restored value the moment the visitor reaches for the control,
         before the click lands, so what they see is what they act on. */
      document.addEventListener('pointerdown', function (e) {
        if (e.target.closest && e.target.closest('.lp-qty')) syncQty();
      }, true);
      document.addEventListener('focusin', function (e) {
        if (e.target.closest && e.target.closest('.lp-qty')) syncQty();
      });

      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-lp-qty-up], [data-lp-qty-down]');
        if (!btn) return;

        var input = btn.parentElement.querySelector('.lp-qty__input');
        if (!input) return;

        var step = btn.hasAttribute('data-lp-qty-up') ? 1 : -1;
        applyQty(clampQty(input, (parseInt(input.value, 10) || 0) + step));
      });

      document.addEventListener('change', function (e) {
        var input = e.target.closest && e.target.closest('.lp-qty__input');
        if (!input) return;
        applyQty(clampQty(input, parseInt(input.value, 10)));
      });

      /* Fires on a normal load and again when the page comes back out of the
         back/forward cache, which is exactly the return trip that leaves a
         restored value behind. */
      window.addEventListener('pageshow', syncQty);
      /* And once more after the current task, for the browsers that restore
         fields a moment after the document is ready. */
      window.setTimeout(syncQty, 0);
    });

    syncQty();
  }

  /* ========================================================================
     7. REVEAL
     ===================================================================== */
  function initReveal() {
    var targets = $$('.lp-reveal');
    if (!targets.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) return;

    // Only hide once we can guarantee we will reveal again.
    root.classList.add('lp-anim');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ========================================================================
     8. CART
     ---------------------------------------------------------------------
     Posts the native product forms to Shopify's Ajax cart API and opens the
     drawer. Only used when the CTA is in add-to-cart mode; buy-now is handled
     above. Checkout, payment methods (including cash on delivery)
     and the order itself all stay with Shopify. If anything throws, the form
     is submitted natively so the visitor still reaches /cart.
     ===================================================================== */
  /* ------------------------------------------------------------------------
     Buy now — hand off to a Shopify cart permalink,
     /cart/<variant>:<quantity>?checkout. Shopify replaces the cart with exactly
     this line and opens checkout, so nothing an earlier visit left behind rides
     along with the order. No API call and no custom order handling.

     Bound on the capture phase at document level so it runs before any other
     submit handler on the form — including the main theme's cart drawer, when
     this file is used alongside it.
     --------------------------------------------------------------------- */
  function initBuyNow() {
    once('buynow', function () {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.classList || !form.classList.contains('lp-buy')) return;
      if (form.getAttribute('data-lp-mode') !== 'buy_now') return;

      var variant = form.querySelector('[name="id"]');
      if (!variant || !variant.value) return; // let the native POST happen

      /* Settle the quantity against the visible stepper before reading it, so
         a value the browser restored after load cannot send this form off to
         checkout with a stale number. */
      syncQty();

      var qtyField = form.querySelector('[name="quantity"]');
      var qtyMax = parseInt(qtyField && qtyField.getAttribute('max'), 10);
      var qty = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);
      if (!isNaN(qtyMax)) qty = Math.min(qtyMax, qty);
      var cartRoute = (ROUTES.cart || '/cart').replace(/\/$/, '');

      e.preventDefault();
      e.stopPropagation();

      var button = form.querySelector('[data-lp-atc]');
      if (button) button.classList.add('is-loading');

      push('landing_begin_checkout', {
        cta: button ? button.getAttribute('data-lp-context') : 'landing',
        variant_id: variant.value,
        quantity: qty
      });

      window.location.href = cartRoute + '/' + variant.value + ':' + qty + '?checkout';
    }, true);
    });
  }

  var Cart = {
    init: function () {
      this.drawer = $('[data-lp-cart]');
      this.overlay = $('[data-lp-cart-overlay]');
      this.body = $('[data-lp-cart-body]');
      this.foot = $('[data-lp-cart-foot]');
      this.totalEl = $('[data-lp-cart-total]');
      this.counts = $$('[data-lp-cart-count]');

      var self = this;

      $$('form.lp-buy').forEach(function (form) {
        form.addEventListener('submit', function (e) { self.add(e, form); });
      });

      if (!this.drawer) return;

      $$('[data-lp-cart-open]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); self.open(); });
      });
      $$('[data-lp-cart-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });
      if (this.overlay) this.overlay.addEventListener('click', function () { self.close(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen()) self.close();
      });

      this.drawer.addEventListener('click', function (e) {
        var remove = e.target.closest('[data-lp-cart-remove]');
        if (remove) { self.update(remove.dataset.key, 0); return; }
        var inc = e.target.closest('[data-lp-cart-inc]');
        if (inc) { self.update(inc.dataset.key, parseInt(inc.dataset.qty, 10) + 1); return; }
        var dec = e.target.closest('[data-lp-cart-dec]');
        if (dec) { self.update(dec.dataset.key, parseInt(dec.dataset.qty, 10) - 1); }
      });

      this.fetch().then(function (cart) { self.render(cart); }).catch(function () {});
    },

    isOpen: function () { return this.drawer.getAttribute('aria-hidden') === 'false'; },

    open: function () {
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lp-no-scroll');
      var close = $('[data-lp-cart-close]', this.drawer);
      if (close) close.focus();
    },

    close: function () {
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lp-no-scroll');
    },

    add: function (e, form) {
      if (!this.drawer) return;
      e.preventDefault();

      var button = form.querySelector('[data-lp-atc]');
      if (button) button.classList.add('is-loading');

      var self = this;
      fetch(route('cart_add', '/cart/add'), {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: new FormData(form)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return self.fetch();
        })
        .then(function (cart) { self.render(cart); self.open(); })
        .catch(function () { form.submit(); })
        .then(function () { if (button) button.classList.remove('is-loading'); });
    },

    fetch: function () {
      return fetch(route('cart', '/cart'), { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); });
    },

    update: function (key, qty) {
      var self = this;
      this.drawer.classList.add('is-loading');
      fetch(route('cart_change', '/cart/change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) { self.render(cart); })
        .catch(function () {})
        .then(function () { self.drawer.classList.remove('is-loading'); });
    },

    render: function (cart) {
      this.counts.forEach(function (el) {
        el.textContent = cart.item_count;
        el.dataset.empty = cart.item_count === 0 ? 'true' : 'false';
      });

      if (!this.body) return;

      if (cart.item_count === 0) {
        this.body.innerHTML = '<div class="lp-cart__empty"><p>' + escapeHtml(STRINGS.cartEmpty || '') + '</p></div>';
        if (this.foot) this.foot.hidden = true;
        return;
      }

      if (this.foot) this.foot.hidden = false;
      if (this.totalEl) this.totalEl.textContent = money(cart.total_price);

      var self = this;
      this.body.innerHTML = cart.items.map(function (item) {
        var img = item.featured_image && item.featured_image.url ? item.featured_image.url : (item.image || '');
        var variant = item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="lp-cart__item-variant">' + escapeHtml(item.variant_title) + '</p>' : '';
        return '' +
          '<div class="lp-cart__item">' +
            (img ? '<img class="lp-cart__item-img" src="' + escapeHtml(img) + '" alt="" loading="lazy" width="72" height="72">' : '<span class="lp-cart__item-img"></span>') +
            '<div>' +
              '<h3 class="lp-cart__item-title">' + escapeHtml(item.product_title) + '</h3>' + variant +
              '<div class="lp-cart__qty">' +
                '<button type="button" data-lp-cart-dec data-key="' + escapeHtml(item.key) + '" data-qty="' + item.quantity + '" aria-label="' + escapeHtml(STRINGS.decrease || '-') + '">&minus;</button>' +
                '<input type="text" value="' + item.quantity + '" readonly aria-label="' + escapeHtml(STRINGS.quantity || '') + '">' +
                '<button type="button" data-lp-cart-inc data-key="' + escapeHtml(item.key) + '" data-qty="' + item.quantity + '" aria-label="' + escapeHtml(STRINGS.increase || '+') + '">+</button>' +
              '</div>' +
              '<button type="button" class="lp-cart__remove" data-lp-cart-remove data-key="' + escapeHtml(item.key) + '">' + escapeHtml(STRINGS.remove || '') + '</button>' +
            '</div>' +
            '<div class="lp-cart__item-price">' + money(item.final_line_price) + '</div>' +
          '</div>';
      }).join('');

      void self;
    }
  };

  function boot() {
    initHeader();
    initAnchors();
    initGallery();
    initNavHighlight();
    initFaq();
    initStickyCta();
    initQuantity();
    initBuyNow();
    initReveal();
    initAnalytics();
    Cart.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Theme editor: re-run when a section is re-rendered. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
  }
})();
